/* global mainScript */
import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { useState, useEffect } from "react";
import moment from 'moment';

//style rules
import { Sizes, Animation, Colors } from "../../styles";

const CalendarEventForm = ({event, setFormOpen, date, ticketNumber, setTicketNumber, audioguide, setAudioguide}) => {

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.large})`,
	});

	return (
		<>
			<Overlay
				initial={"closed"}
				animate={"opened"}
				exit={"closed"}
				variants={Animation.sidepanel.overlayVariants}
				onClick={() => setFormOpen(false)}
			/>
			<Aside
				initial={{ width: 0, opacity: 0 }}
				animate={{ width: isDesktopOrLaptop ? 949 : "100vw", opacity: 1, transition: { delay: 0.3 } }}
				exit={{
					width: 0,
					opacity: 0,
					transition: { delay: 0.5 },
				}}
			>
				<PanelContainer>
					<CloseIcon
						aria-label={"close sidepanel"}
						initial={"closed"}
						animate={"open"}
						exit={"exit"}
						variants={Animation.sidepanel.opacityVariants}
						icon={mainScript.assetPath + "images/svg/icons/icon-close.svg"}
						onClick={() => setFormOpen(false)}
						onKeyDown={() => setFormOpen(false)}
						tabIndex={0}
					/>
					<Wrapper
						initial={"closed"}
						animate={"open"}
						exit={"closed"}
						variants={Animation.sidepanel.opacityVariants}
					>
						<Header>
							<HeaderTitle>Ticket options</HeaderTitle>
							<h3>{moment(date).format('dddd, MMMM Do')}</h3>
						</Header>
						<ContentContainer>
							<div className="form">
								<div className="tickets">
									<div className="tickets__title">
										<h3>Number of Tickets</h3>
									</div>
									<div className="tickets__wrap">
										<button aria-label="remove one ticket" disabled={ticketNumber === 0} type="button" className="tickets--minus" onClick={() => ticketNumber > 0 && setTicketNumber(ticketNumber - 1)}>-</button>
										<input type="text" className="tickets__input" value={ticketNumber} aria-label="amount of tickets" max={event.ticketNumber} onChange={(e) => setTicketNumber(Math.max(0, Math.min(event.availability, +e.target.value)))} />
										<button aria-label="add one ticket" disabled={ticketNumber >= event.availability} className="tickets--plus" type="button" onClick={() => setTicketNumber(ticketNumber + 1)}>+</button>
									</div>
									<div className="tickets__text">
										<p>For groups of more than 55 people please submit a reservation <a>request form</a></p>
									</div>
								</div>

								{event.type === 'hsv' && event.ag_availability > 0 && (
									<div className="audioguide">
										<div className="audioguide__title">
											<h3>Audioguide</h3>
											<p>cost $5.00</p>
										</div>
										<div className="audioguide__wrap">
											<button aria-label="remove one audioguide" disabled={ticketNumber === 0} type="button" className="audioguide--minus" onClick={() => audioguide > 0 && setAudioguide(audioguide - 1)}>-</button>
											<input type="text" className="audioguide__input" value={audioguide} aria-label="amount of audioguides" max={event.ag_availability} onChange={(e) => +e.target.value <= ticketNumber &&  +e.target.value <= event.ag_availability ? setAudioguide(+e.target.value) : setAudioguide(ticketNumber)} />
											<button disabled={audioguide === ticketNumber || audioguide === event.ag_availability} aria-label="add one audioguide" className="audioguide--plus" type="button" onClick={() => audioguide + 1 <= ticketNumber && audioguide + 1 <= event.ag_availability && setAudioguide(audioguide + 1)}>+</button>
										</div>
									</div>
								)}

								<button className="form__submit" type="submit">submit</button>
							</div>
						</ContentContainer>
					</Wrapper>

				</PanelContainer>
			</Aside>
		</>
	)
}

export default CalendarEventForm;

const Aside = styled(motion.aside)`
	background-color: ${Colors.base.white};
	height: 100vh;
	position: fixed;
	right: 0;
	top: 0;
	height: 100vh;
	overflow: visible;
	z-index: 1000;
`;

const PanelContainer = styled.div `
	height: 100%;
	overflow: scroll;
	padding: 9.2rem 1.6rem 9.2rem;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		padding: 8.5rem 10rem 5rem;
	}
`

const CloseIcon = styled(motion.svg)`
	position: absolute;
	right: 2.5rem;
	top: 2.5rem;
	content: "";
	z-index: 901;
	background: url(${(props) => props.icon && props.icon}) no-repeat;
	width: 40px;
	height: 40px;
	margin: 0.4rem 0 0 0.5rem;
	cursor: pointer;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		right: 5rem;
		top: 5rem;
	}
`;

const Wrapper = styled(motion.div)`
	padding: 9.2rem 1.6rem 4.5;
	height: 100%;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		padding: 14.2rem 10.6rem 8;
	}
`;

const Header = styled.div`
	text-align: left;
	padding-bottom: 6rem;

	h3 {
		margin-top: 2rem;
	}
`;

const HeaderTitle = styled.h2`
	font-size: 3.4rem;
	line-height: 3.8rem;
	text-align: left;
`;

const ContentContainer = styled.section`
	height: calc(100% - 7.8rem);
`;

const AspectRatioBox = styled.div`
	position: relative;
	width: 100%;
	display: block;
	overflow: hidden;

	padding-top: calc(165.48 / 236.28 * 100%);

	> img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center center;
		border-radius: 10px;
		transition: all 0.5s;
	}
`;

const Overlay = styled(motion.div)`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	cursor: pointer;
	width: 100vw;
	height: 100vh;
	z-index: 900;
	background: ${Colors.base.black};
	opacity: 0.5;
`;
